import { useCallback, useEffect, useState } from "react";

/**
 * useThemeMode — mirrors the mobile app's ThemeContext contract.
 * Reads/writes the same localStorage key so a user's preference carries
 * across the two surfaces. Toggles the `dark` class on <html> so both the
 * shadcn CSS variables and Tailwind `dark:` prefixes react together.
 */
const KEY = "mem_theme_mode";
const DEFAULT_MODE = "dark";

function apply(mode) {
  if (typeof document === "undefined") return;
  const el = document.documentElement;
  if (mode === "dark") el.classList.add("dark");
  else el.classList.remove("dark");
  el.style.colorScheme = mode;
}

export function useThemeMode() {
  const [mode, setModeState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_MODE;
    const saved = window.localStorage.getItem(KEY);
    return saved === "light" || saved === "dark" ? saved : DEFAULT_MODE;
  });

  useEffect(() => { apply(mode); }, [mode]);

  const setMode = useCallback((m) => {
    setModeState(m);
    try { window.localStorage.setItem(KEY, m); } catch { /* ignore */ }
  }, []);

  const toggle = useCallback(() => {
    setModeState((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      try { window.localStorage.setItem(KEY, next); } catch { /* ignore */ }
      return next;
    });
  }, []);

  return { mode, setMode, toggle };
}
